import { REPORT_DEFINITIONS } from "./reportSchemas";
import type {
  AnalysisSubmission,
  AnalysisTask,
  AnalysisTaskQuery,
  AvailableFinancialReport,
  CompleteReportResponse,
  FinancialAnalysisService,
  FinancialBillingStatus,
  FinancialProductCode,
  FinancialReportResponse,
  FinancialReportType,
  IndustryOption,
  ReportTaskStatus,
} from "./types";

export type PlatformRequest = <T>(path: string, init?: RequestInit) => Promise<T>;

interface PlatformReport {
  id: string;
  type: FinancialReportType;
  status: string;
  price: string;
  billingStatus?: string;
  reportName?: string | null;
  error?: string | null;
}

interface PlatformTask {
  id: string;
  enterpriseName: string;
  period: string;
  status: string;
  expectedAmount: string;
  chargedAmount: string;
  createdAt?: string;
  finishedAt?: string | null;
  reports: PlatformReport[];
  products?: Array<{ productCode: FinancialProductCode; resultType: FinancialReportType | "complete"; status: string; price: string; billingStatus?: string; error?: string | null }>;
}

const taskTimestampFormatter = new Intl.DateTimeFormat("en-CA", {
  timeZone: "Asia/Shanghai",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23",
});

export class PlatformFinancialAnalysisService implements FinancialAnalysisService {
  constructor(private readonly request: PlatformRequest) {}

  async listAvailableReports(): Promise<AvailableFinancialReport[]> {
    const reports = await this.request<Array<Omit<AvailableFinancialReport, "price"> & { price: string | null }>>(
      "/customer/financial-analysis/catalog",
    );
    return reports.map((report) => ({
      ...report,
      price: report.price === null ? null : Number(report.price),
    }));
  }

  async listIndustries(query = ""): Promise<IndustryOption[]> {
    const suffix = query ? `?query=${encodeURIComponent(query)}` : "";
    return this.request<IndustryOption[]>(`/customer/financial-analysis/industries${suffix}`);
  }

  async submitAnalysis(input: AnalysisSubmission): Promise<AnalysisTask> {
    const body = new FormData();
    body.set("metadata", JSON.stringify({
      corpCode: input.customerCode,
      corpName: input.enterpriseName,
      enterpriseSize: input.enterpriseSize,
      industryCode: input.industryCode,
      industryName: input.industryName,
      period: input.period,
      reportPeriodType: input.reportPeriodType,
      amountUnit: input.amountUnit,
      ...(input.productCodes?.length
        ? { productCodes: input.productCodes }
        : { reportTypes: input.reportTypes }),
      ...(input.annualStatements?.length
        ? { annualYears: input.annualStatements.map((statement) => statement.year) }
        : {}),
    }));
    (input.annualStatements?.map((statement) => statement.file) ?? input.statements)
      .forEach((file) => body.append("statements", file));
    return normalizeTask(await this.request<PlatformTask>(
      "/customer/financial-analysis/tasks",
      { method: "POST", body },
    ));
  }

  async listTasks(query: AnalysisTaskQuery = {}): Promise<AnalysisTask[]> {
    const tasks = await this.request<PlatformTask[]>("/customer/financial-analysis/tasks");
    const keyword = query.keyword?.trim().toLowerCase() ?? "";
    return tasks.map(normalizeTask).filter((task) => (
      (!keyword || `${task.enterpriseName}${task.id}`.toLowerCase().includes(keyword))
      && (!query.status || query.status === "all" || task.status === query.status)
    ));
  }

  async getTask(taskId: string): Promise<AnalysisTask> {
    return normalizeTask(await this.request<PlatformTask>(
      `/customer/financial-analysis/tasks/${taskId}`,
    ));
  }

  async getReport(
    taskId: string,
    reportType: FinancialReportType,
  ): Promise<FinancialReportResponse> {
    const response = await this.request<Record<string, unknown>>(
      `/customer/financial-analysis/tasks/${taskId}/reports/${reportType}`,
    );
    return {
      ...response,
      kind: reportKind(reportType),
      reportType,
    } as FinancialReportResponse;
  }

  async retryReport(taskId: string, reportType: FinancialReportType): Promise<void> {
    await this.request(
      `/customer/financial-analysis/tasks/${taskId}/reports/${reportType}/retry`,
      { method: "POST" },
    );
  }

  async getProduct(taskId: string, productCode: FinancialProductCode): Promise<FinancialReportResponse | CompleteReportResponse> {
    const response = await this.request<Record<string, unknown>>(
      `/customer/financial-analysis/tasks/${taskId}/products/${productCode}`,
    );
    if (productCode === "FINANCIAL_COMPLETE") {
      const complete = response as Omit<CompleteReportResponse, "reports"> & { reports: Array<{ type: FinancialReportType; title: string; response: Record<string, unknown> }> };
      return { ...complete, kind: "complete", productCode: "FINANCIAL_COMPLETE", reports: complete.reports.map((report) => ({ ...report, response: { ...report.response, kind: reportKind(report.type), reportType: report.type } as FinancialReportResponse })) };
    }
    const reportType = productCode.replace("FINANCIAL_", "").toLowerCase().replaceAll("_", "-") as FinancialReportType;
    return { ...response, kind: reportKind(reportType), reportType } as FinancialReportResponse;
  }

  async retryProduct(taskId: string, productCode: FinancialProductCode): Promise<void> {
    await this.request(`/customer/financial-analysis/tasks/${taskId}/products/${productCode}/retry`, { method: "POST" });
  }
}

function normalizeTask(task: PlatformTask): AnalysisTask {
  return {
    id: task.id,
    enterpriseName: task.enterpriseName,
    period: task.period,
    submittedAt: formatTaskTimestamp(task.createdAt) ?? "刚刚",
    completedAt: formatTaskTimestamp(task.finishedAt),
    status: normalizeStatus(task.status),
    expectedAmount: Number(task.expectedAmount),
    chargedAmount: Number(task.chargedAmount),
    reports: task.reports.map((report) => ({
      id: report.id,
      type: report.type,
      status: normalizeStatus(report.status),
      reportName: report.reportName || REPORT_DEFINITIONS[report.type].title,
      price: Number(report.price),
      billingStatus: normalizeBillingStatus(report.billingStatus),
      error: report.error ?? undefined,
    })),
    products: task.products?.map((product) => ({
      productCode: product.productCode,
      resultType: product.resultType,
      status: normalizeStatus(product.status),
      price: Number(product.price),
      billingStatus: normalizeBillingStatus(product.billingStatus),
      error: product.error ?? undefined,
    })),
  };
}

function formatTaskTimestamp(value?: string | null): string | undefined {
  if (!value) return undefined;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const parts = taskTimestampFormatter.formatToParts(date).reduce<Record<string, string>>((result, part) => {
    if (part.type !== "literal") result[part.type] = part.value;
    return result;
  }, {});
  return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}:${parts.second}`;
}

function normalizeBillingStatus(status?: string): FinancialBillingStatus {
  if (status === "BILLED") return "billed";
  if (status === "FREE") return "free";
  return "not_billed";
}

function normalizeStatus(status: string): ReportTaskStatus {
  const values: Record<string, ReportTaskStatus> = {
    QUEUED: "queued",
    RUNNING: "running",
    SUCCEEDED: "completed",
    FAILED: "failed",
    UNKNOWN: "unknown",
    REVIEW: "unknown",
    PARTIAL: "partial",
  };
  return values[status] ?? "queued";
}

function reportKind(reportType: FinancialReportType): FinancialReportResponse["kind"] {
  if (reportType === "dressing") return "dressing";
  if (reportType === "account-movement") return "account-movement";
  if (reportType === "industry-benchmark") return "industry-comparison";
  return "common";
}
